import jsPDF from 'jspdf';

// Decoded Logo as base64 (simple text-based logo for PDF)
const DECODED_LOGO_TEXT = 'DECODED';
const DECODED_TAGLINE = 'AI-Powered Tariff Classification';

// Disclaimer text
const DISCLAIMER_TEXT = `DISCLAIMER: This report is generated by Decoded's AI-powered classification engine for informational purposes only. 
The suggested HS codes and tariff rates are recommendations based on product descriptions and should be verified by a licensed customs broker 
or trade compliance professional before use in official customs declarations. Decoded assumes no liability for any decisions made based on this report. 
Tariff rates and trade regulations are subject to change. Always consult official government sources for the most current information.`;

// Colors
const COLORS = {
  primary: [37, 99, 235],      // #2563EB
  secondary: [6, 182, 212],    // #06B6D4
  success: [5, 150, 105],      // #059669
  dark: [15, 23, 42],          // #0F172A
  gray: [100, 116, 139],       // #64748B
  lightGray: [226, 232, 240],  // #E2E8F0
  white: [255, 255, 255]
};

/**
 * Add header with logo to a PDF page
 */
export const addPdfHeader = (pdf, pageNumber, totalPages) => {
  const pageWidth = pdf.internal.pageSize.getWidth();
  
  // Header background
  pdf.setFillColor(...COLORS.primary);
  pdf.rect(0, 0, pageWidth, 20, 'F');
  
  // Logo text
  pdf.setTextColor(...COLORS.white);
  pdf.setFontSize(16);
  pdf.setFont('helvetica', 'bold');
  pdf.text(DECODED_LOGO_TEXT, 15, 13);
  
  // Tagline
  pdf.setFontSize(8);
  pdf.setFont('helvetica', 'normal');
  pdf.text(DECODED_TAGLINE, 50, 13);
  
  // Page number
  pdf.setFontSize(9);
  pdf.text(`Page ${pageNumber} of ${totalPages}`, pageWidth - 35, 13);
  
  // Reset text color
  pdf.setTextColor(...COLORS.dark);
};

/**
 * Add footer with disclaimer to a PDF page
 */
export const addPdfFooter = (pdf, isLastPage = false) => {
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  
  // Footer line
  pdf.setDrawColor(...COLORS.lightGray);
  pdf.setLineWidth(0.5);
  pdf.line(15, pageHeight - 25, pageWidth - 15, pageHeight - 25);
  
  // Footer text
  pdf.setFontSize(7);
  pdf.setTextColor(...COLORS.gray);
  
  const currentDate = new Date().toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });
  
  pdf.text(`Generated by Decoded on ${currentDate}`, 15, pageHeight - 20);
  pdf.text('www.decoded.ai', pageWidth - 40, pageHeight - 20);
  
  // Disclaimer on last page (or every page if needed)
  if (isLastPage) {
    pdf.setFontSize(6);
    pdf.setTextColor(...COLORS.gray);
    const disclaimerLines = pdf.splitTextToSize(DISCLAIMER_TEXT, pageWidth - 30);
    pdf.text(disclaimerLines, 15, pageHeight - 15);
  }
};

/**
 * Add a section title
 */
export const addSectionTitle = (pdf, title, yPos) => {
  pdf.setFontSize(14);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(...COLORS.primary);
  pdf.text(title, 15, yPos);
  
  // Underline
  const titleWidth = pdf.getTextWidth(title);
  pdf.setDrawColor(...COLORS.secondary);
  pdf.setLineWidth(1);
  pdf.line(15, yPos + 2, 15 + titleWidth, yPos + 2);
  
  pdf.setTextColor(...COLORS.dark);
  return yPos + 10;
};

/**
 * Add key-value pair
 */
export const addKeyValue = (pdf, key, value, yPos, xOffset = 15) => {
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(...COLORS.gray);
  pdf.text(`${key}:`, xOffset, yPos);
  
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(...COLORS.dark);
  const keyWidth = pdf.getTextWidth(`${key}: `);
  pdf.text(String(value), xOffset + keyWidth, yPos);
  
  return yPos + 6;
};

/**
 * Check if we need a new page and add it if necessary
 */
export const checkPageBreak = (pdf, currentY, requiredSpace, pageNumber, totalPages) => {
  const pageHeight = pdf.internal.pageSize.getHeight();
  const marginBottom = 35; // Space for footer
  
  if (currentY + requiredSpace > pageHeight - marginBottom) {
    // Add footer to current page
    addPdfFooter(pdf, false);
    
    // Add new page
    pdf.addPage();
    pageNumber++;
    
    // Add header to new page
    addPdfHeader(pdf, pageNumber, totalPages);
    
    return { y: 30, pageNumber }; // Start content below header
  }
  
  return { y: currentY, pageNumber };
};

/**
 * Generate a professional PDF report for analysis results
 */
export const generateAnalysisPdf = async (analysis, results) => {
  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4'
  });
  
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  
  // Calculate total pages (estimate)
  const itemsPerPage = 8;
  const totalPages = Math.max(2, Math.ceil(results.length / itemsPerPage) + 1);
  let currentPage = 1;
  
  // ===== PAGE 1: Cover & Summary =====
  addPdfHeader(pdf, currentPage, totalPages);
  
  let y = 35;
  
  // Report Title
  pdf.setFontSize(24);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(...COLORS.dark);
  pdf.text('Classification Analysis Report', 15, y);
  y += 15;
  
  // Analysis Info Box
  pdf.setFillColor(248, 250, 252);
  pdf.roundedRect(15, y, pageWidth - 30, 35, 3, 3, 'F');
  y += 8;
  
  y = addKeyValue(pdf, 'Analysis ID', analysis.analysis_id || 'N/A', y);
  y = addKeyValue(pdf, 'Date', new Date(analysis.created_at).toLocaleDateString(), y);
  y = addKeyValue(pdf, 'Total Products', results.length, y);
  y = addKeyValue(pdf, 'Type', (analysis.type || 'single').replace('_', ' ').toUpperCase(), y);
  
  y += 10;
  
  // Summary Section
  y = addSectionTitle(pdf, 'Executive Summary', y);
  
  const totalSavings = results.reduce((sum, r) => sum + (r.estimated_savings > 0 ? r.estimated_savings : 0), 0);
  const complianceRisk = results.reduce((sum, r) => sum + (r.estimated_savings < 0 ? Math.abs(r.estimated_savings) : 0), 0);
  const avgConfidence = results.length > 0 ? results.reduce((sum, r) => sum + (r.confidence_score || 0), 0) / results.length : 0;
  
  // Stats boxes
  const boxWidth = (pageWidth - 40) / 3;
  
  // Savings Box
  pdf.setFillColor(...COLORS.success);
  pdf.roundedRect(15, y, boxWidth, 25, 2, 2, 'F');
  pdf.setTextColor(...COLORS.white);
  pdf.setFontSize(8);
  pdf.text('POTENTIAL SAVINGS', 17, y + 6);
  pdf.setFontSize(14);
  pdf.setFont('helvetica', 'bold');
  pdf.text(`$${totalSavings.toLocaleString()}`, 17, y + 18);
  
  // Risk Box
  pdf.setFillColor(239, 68, 68);
  pdf.roundedRect(20 + boxWidth, y, boxWidth, 25, 2, 2, 'F');
  pdf.setTextColor(...COLORS.white);
  pdf.setFontSize(8);
  pdf.setFont('helvetica', 'normal');
  pdf.text('COMPLIANCE RISK', 22 + boxWidth, y + 6);
  pdf.setFontSize(14);
  pdf.setFont('helvetica', 'bold');
  pdf.text(`$${complianceRisk.toLocaleString()}`, 22 + boxWidth, y + 18);
  
  // Confidence Box
  pdf.setFillColor(...COLORS.primary);
  pdf.roundedRect(25 + boxWidth * 2, y, boxWidth, 25, 2, 2, 'F');
  pdf.setTextColor(...COLORS.white);
  pdf.setFontSize(8);
  pdf.setFont('helvetica', 'normal');
  pdf.text('AVG CONFIDENCE', 27 + boxWidth * 2, y + 6);
  pdf.setFontSize(14);
  pdf.setFont('helvetica', 'bold');
  pdf.text(`${(avgConfidence * 100).toFixed(0)}%`, 27 + boxWidth * 2, y + 18);
  
  y += 35;
  
  // Classification Results Section
  y = addSectionTitle(pdf, 'Classification Results', y);
  
  // Table Header
  pdf.setFillColor(...COLORS.primary);
  pdf.rect(15, y, pageWidth - 30, 8, 'F');
  pdf.setTextColor(...COLORS.white);
  pdf.setFontSize(8);
  pdf.setFont('helvetica', 'bold');
  pdf.text('SKU / Description', 17, y + 5.5);
  pdf.text('Current HS', 80, y + 5.5);
  pdf.text('Suggested HS', 110, y + 5.5);
  pdf.text('Confidence', 145, y + 5.5);
  pdf.text('Impact', 175, y + 5.5);
  
  y += 10;
  
  // Table rows
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(...COLORS.dark);
  
  for (let i = 0; i < results.length; i++) {
    const result = results[i];
    
    // Check for page break
    if (y > pageHeight - 45) {
      addPdfFooter(pdf, false);
      pdf.addPage();
      currentPage++;
      addPdfHeader(pdf, currentPage, totalPages);
      y = 30;
      
      // Re-add table header
      pdf.setFillColor(...COLORS.primary);
      pdf.rect(15, y, pageWidth - 30, 8, 'F');
      pdf.setTextColor(...COLORS.white);
      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'bold');
      pdf.text('SKU / Description', 17, y + 5.5);
      pdf.text('Current HS', 80, y + 5.5);
      pdf.text('Suggested HS', 110, y + 5.5);
      pdf.text('Confidence', 145, y + 5.5);
      pdf.text('Impact', 175, y + 5.5);
      y += 10;
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(...COLORS.dark);
    }
    
    // Alternating row background
    if (i % 2 === 0) {
      pdf.setFillColor(248, 250, 252);
      pdf.rect(15, y - 1, pageWidth - 30, 12, 'F');
    }
    
    pdf.setFontSize(7);
    const description = (result.description || result.sku_id || 'N/A').substring(0, 35);
    pdf.text(description, 17, y + 5);
    
    pdf.text(result.current_hs_code || '-', 80, y + 5);
    pdf.text(result.suggested_hs_code || '-', 110, y + 5);
    
    const confidence = ((result.confidence_score || 0) * 100).toFixed(0) + '%';
    pdf.text(confidence, 145, y + 5);
    
    const impact = result.estimated_savings || 0;
    if (impact > 0) {
      pdf.setTextColor(...COLORS.success);
      pdf.text(`+$${impact.toLocaleString()}`, 175, y + 5);
    } else if (impact < 0) {
      pdf.setTextColor(239, 68, 68);
      pdf.text(`-$${Math.abs(impact).toLocaleString()}`, 175, y + 5);
    } else {
      pdf.setTextColor(...COLORS.gray);
      pdf.text('$0', 175, y + 5);
    }
    pdf.setTextColor(...COLORS.dark);
    
    y += 12;
  }
  
  // Add footer with disclaimer to last page
  addPdfFooter(pdf, true);
  
  return pdf;
};

/**
 * Generate PDF for Financial Impact Analysis
 */
export const generateFinancialPdf = async (analysis) => {
  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4'
  });
  
  const pageWidth = pdf.internal.pageSize.getWidth();
  const results = analysis.results || [];
  const summary = analysis.summary || {};
  
  const totalPages = Math.max(2, Math.ceil(results.length / 6) + 1);
  let currentPage = 1;
  
  // Page 1: Cover
  addPdfHeader(pdf, currentPage, totalPages);
  
  let y = 35;
  
  // Title
  pdf.setFontSize(24);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(...COLORS.dark);
  pdf.text('Financial Impact Analysis', 15, y);
  y += 12;
  
  pdf.setFontSize(12);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(...COLORS.gray);
  pdf.text('Duty Exposure & Optimization Report', 15, y);
  y += 15;
  
  // Summary Stats
  y = addSectionTitle(pdf, 'Executive Summary', y);
  
  const boxWidth = (pageWidth - 40) / 2;
  
  // Row 1
  pdf.setFillColor(...COLORS.success);
  pdf.roundedRect(15, y, boxWidth, 30, 2, 2, 'F');
  pdf.setTextColor(...COLORS.white);
  pdf.setFontSize(9);
  pdf.text('TOTAL OVERPAYMENT EXPOSURE', 17, y + 8);
  pdf.setFontSize(18);
  pdf.setFont('helvetica', 'bold');
  pdf.text(`$${(summary.total_overpayment_exposure || 0).toLocaleString()}`, 17, y + 22);
  
  pdf.setFillColor(239, 68, 68);
  pdf.roundedRect(20 + boxWidth, y, boxWidth, 30, 2, 2, 'F');
  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'normal');
  pdf.text('UNDERPAYMENT RISK', 22 + boxWidth, y + 8);
  pdf.setFontSize(18);
  pdf.setFont('helvetica', 'bold');
  pdf.text(`$${(summary.total_underpayment_risk || 0).toLocaleString()}`, 22 + boxWidth, y + 22);
  
  y += 40;
  
  // Row 2
  pdf.setFillColor(...COLORS.primary);
  pdf.roundedRect(15, y, boxWidth, 25, 2, 2, 'F');
  pdf.setTextColor(...COLORS.white);
  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'normal');
  pdf.text('SKUs ANALYZED', 17, y + 8);
  pdf.setFontSize(14);
  pdf.setFont('helvetica', 'bold');
  pdf.text(String(summary.total_skus_analyzed || 0), 17, y + 18);
  
  pdf.setFillColor(...COLORS.secondary);
  pdf.roundedRect(20 + boxWidth, y, boxWidth, 25, 2, 2, 'F');
  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'normal');
  pdf.text('MISCLASSIFICATION RATE', 22 + boxWidth, y + 8);
  pdf.setFontSize(14);
  pdf.setFont('helvetica', 'bold');
  pdf.text(`${summary.misclassification_rate || 0}%`, 22 + boxWidth, y + 18);
  
  y += 35;
  
  // Results table
  y = addSectionTitle(pdf, 'Top Impact SKUs', y);
  
  // Table header
  pdf.setFillColor(...COLORS.primary);
  pdf.rect(15, y, pageWidth - 30, 8, 'F');
  pdf.setTextColor(...COLORS.white);
  pdf.setFontSize(7);
  pdf.setFont('helvetica', 'bold');
  pdf.text('SKU', 17, y + 5.5);
  pdf.text('Declared HS', 50, y + 5.5);
  pdf.text('Suggested HS', 80, y + 5.5);
  pdf.text('Duty Delta', 110, y + 5.5);
  pdf.text('Exposure', 140, y + 5.5);
  pdf.text('Priority', 170, y + 5.5);
  
  y += 10;
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(...COLORS.dark);
  
  const sortedResults = [...results].sort((a, b) => (b.annual_exposure || 0) - (a.annual_exposure || 0));
  
  for (let i = 0; i < sortedResults.length; i++) {
    const result = sortedResults[i];
    
    // Check for page break
    if (y > pdf.internal.pageSize.getHeight() - 45) {
      addPdfFooter(pdf, false);
      pdf.addPage();
      currentPage++;
      addPdfHeader(pdf, currentPage, totalPages);
      y = 30;
      
      // Re-add table header
      pdf.setFillColor(...COLORS.primary);
      pdf.rect(15, y, pageWidth - 30, 8, 'F');
      pdf.setTextColor(...COLORS.white);
      pdf.setFontSize(7);
      pdf.setFont('helvetica', 'bold');
      pdf.text('SKU', 17, y + 5.5);
      pdf.text('Declared HS', 50, y + 5.5);
      pdf.text('Suggested HS', 80, y + 5.5);
      pdf.text('Duty Delta', 110, y + 5.5);
      pdf.text('Exposure', 140, y + 5.5);
      pdf.text('Priority', 170, y + 5.5);
      y += 10;
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(...COLORS.dark);
    }
    
    if (i % 2 === 0) {
      pdf.setFillColor(248, 250, 252);
      pdf.rect(15, y - 1, pageWidth - 30, 10, 'F');
    }
    
    pdf.setFontSize(7);
    pdf.text((result.sku_id || '').substring(0, 18), 17, y + 5);
    pdf.text(result.declared_hs_code || '-', 50, y + 5);
    pdf.text(result.suggested_hs_code || '-', 80, y + 5);
    
    const delta = result.duty_rate_delta || 0;
    pdf.setTextColor(delta > 0 ? COLORS.success[0] : delta < 0 ? 239 : COLORS.gray[0], 
                     delta > 0 ? COLORS.success[1] : delta < 0 ? 68 : COLORS.gray[1],
                     delta > 0 ? COLORS.success[2] : delta < 0 ? 68 : COLORS.gray[2]);
    pdf.text(`${delta > 0 ? '+' : ''}${delta.toFixed(1)}%`, 110, y + 5);
    
    pdf.setTextColor(...COLORS.dark);
    pdf.text(`$${(result.annual_exposure || 0).toLocaleString()}`, 140, y + 5);
    
    const priority = result.priority_level || 'LOW';
    const priorityColor = priority === 'HIGH' ? [239, 68, 68] : priority === 'MEDIUM' ? [245, 158, 11] : COLORS.gray;
    pdf.setTextColor(...priorityColor);
    pdf.text(priority, 170, y + 5);
    pdf.setTextColor(...COLORS.dark);
    
    y += 10;
  }
  
  addPdfFooter(pdf, true);
  
  return pdf;
};

/**
 * Generate PDF for Refund Radar scan results
 */
export const generateRefundRadarPdf = async (scan) => {
  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const results = scan.results || [];
  const totalPages = Math.max(2, Math.ceil(results.length / 5) + 1);
  let currentPage = 1;

  addPdfHeader(pdf, currentPage, totalPages);
  let y = 35;

  // Title
  pdf.setFontSize(22);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(...COLORS.dark);
  pdf.text('Refund Radar Report', 15, y);
  y += 10;
  pdf.setFontSize(11);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(...COLORS.gray);
  pdf.text(`Scan ID: ${scan.run_id || 'N/A'}  |  ${new Date(scan.created_at || Date.now()).toLocaleDateString()}`, 15, y);
  y += 15;

  // Summary
  y = addSectionTitle(pdf, 'Summary', y);
  const summary = scan.summary || {};
  y = addKeyValue(pdf, 'Entries Scanned', String(summary.total_entries || results.length), y);
  y = addKeyValue(pdf, 'Refund Opportunities', String(summary.opportunities_found || results.filter(r => r.refund_eligible).length), y);
  y = addKeyValue(pdf, 'Total Potential Recovery', `$${(summary.total_potential_refund || 0).toLocaleString()}`, y);
  y += 8;

  // Results table
  y = addSectionTitle(pdf, 'Findings', y);
  pdf.setFillColor(...COLORS.primary);
  pdf.rect(15, y, pageWidth - 30, 8, 'F');
  pdf.setTextColor(...COLORS.white);
  pdf.setFontSize(7);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Product', 17, y + 5.5);
  pdf.text('Declared HS', 60, y + 5.5);
  pdf.text('Suggested HS', 90, y + 5.5);
  pdf.text('Overpayment', 125, y + 5.5);
  pdf.text('Eligible', 160, y + 5.5);
  y += 10;
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(...COLORS.dark);

  for (let i = 0; i < results.length; i++) {
    const r = results[i];
    if (y > pdf.internal.pageSize.getHeight() - 45) {
      addPdfFooter(pdf, false);
      pdf.addPage(); currentPage++;
      addPdfHeader(pdf, currentPage, totalPages);
      y = 30;
    }
    if (i % 2 === 0) { pdf.setFillColor(248, 250, 252); pdf.rect(15, y - 1, pageWidth - 30, 10, 'F'); }
    pdf.setFontSize(7);
    pdf.setTextColor(...COLORS.dark);
    pdf.text((r.description || r.product_name || '').substring(0, 28), 17, y + 5);
    pdf.text(r.declared_hs_code || '-', 60, y + 5);
    pdf.text(r.suggested_hs_code || '-', 90, y + 5);
    const amt = r.estimated_overpayment || 0;
    if (amt > 0) { pdf.setTextColor(...COLORS.success); } else { pdf.setTextColor(...COLORS.gray); }
    pdf.text(`$${amt.toLocaleString()}`, 125, y + 5);
    if (r.refund_eligible) { pdf.setTextColor(...COLORS.success); } else { pdf.setTextColor(...COLORS.gray); }
    pdf.text(r.refund_eligible ? 'Yes' : 'No', 160, y + 5);
    pdf.setTextColor(...COLORS.dark);
    y += 10;
  }

  addPdfFooter(pdf, true);
  return pdf;
};

/**
 * Generate PDF for Duty Risk Monitoring watchlist
 */
export const generateDutyRiskPdf = async (watchlist, alerts, dashboard) => {
  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const totalPages = Math.max(2, Math.ceil(watchlist.length / 6) + 1);
  let currentPage = 1;

  addPdfHeader(pdf, currentPage, totalPages);
  let y = 35;

  pdf.setFontSize(22);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(...COLORS.dark);
  pdf.text('Duty Risk Monitoring Report', 15, y);
  y += 10;
  pdf.setFontSize(11);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(...COLORS.gray);
  pdf.text(`Generated: ${new Date().toLocaleDateString()}`, 15, y);
  y += 15;

  // Dashboard summary
  if (dashboard) {
    y = addSectionTitle(pdf, 'Dashboard Summary', y);
    y = addKeyValue(pdf, 'Items Monitored', String(dashboard.total_items || watchlist.length), y);
    y = addKeyValue(pdf, 'Changes Detected', String(dashboard.changes_detected || 0), y);
    y = addKeyValue(pdf, 'Active Alerts', String(dashboard.active_alerts || alerts.length), y);
    y += 8;
  }

  // Watchlist table
  y = addSectionTitle(pdf, 'Watchlist Items', y);
  pdf.setFillColor(...COLORS.primary);
  pdf.rect(15, y, pageWidth - 30, 8, 'F');
  pdf.setTextColor(...COLORS.white);
  pdf.setFontSize(7);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Item', 17, y + 5.5);
  pdf.text('HS Code', 65, y + 5.5);
  pdf.text('Origin', 95, y + 5.5);
  pdf.text('Destination', 120, y + 5.5);
  pdf.text('Status', 155, y + 5.5);
  y += 10;
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(...COLORS.dark);

  for (let i = 0; i < watchlist.length; i++) {
    const item = watchlist[i];
    if (y > pdf.internal.pageSize.getHeight() - 45) {
      addPdfFooter(pdf, false); pdf.addPage(); currentPage++;
      addPdfHeader(pdf, currentPage, totalPages); y = 30;
    }
    if (i % 2 === 0) { pdf.setFillColor(248, 250, 252); pdf.rect(15, y - 1, pageWidth - 30, 10, 'F'); }
    pdf.setFontSize(7);
    pdf.text((item.item_name || '').substring(0, 30), 17, y + 5);
    pdf.text(item.hs_code || '-', 65, y + 5);
    pdf.text((item.origin_country || '-').substring(0, 15), 95, y + 5);
    pdf.text((item.destination_country || '-').substring(0, 15), 120, y + 5);
    const status = item.last_check_status || 'pending';
    const statusColor = status === 'no_change' ? COLORS.success : status === 'change_detected' ? [239, 68, 68] : COLORS.gray;
    pdf.setTextColor(...statusColor);
    pdf.text(status.replace(/_/g, ' '), 155, y + 5);
    pdf.setTextColor(...COLORS.dark);
    y += 10;
  }

  // Alerts
  if (alerts.length > 0) {
    if (y > pdf.internal.pageSize.getHeight() - 60) {
      addPdfFooter(pdf, false); pdf.addPage(); currentPage++;
      addPdfHeader(pdf, currentPage, totalPages); y = 30;
    }
    y += 5;
    y = addSectionTitle(pdf, `Recent Alerts (${alerts.length})`, y);
    for (let i = 0; i < Math.min(alerts.length, 10); i++) {
      const alert = alerts[i];
      if (y > pdf.internal.pageSize.getHeight() - 40) { break; }
      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'bold');
      pdf.text(`${alert.item_name || 'Alert'}: `, 17, y + 5);
      pdf.setFont('helvetica', 'normal');
      pdf.text((alert.message || alert.change_summary || '').substring(0, 80), 55, y + 5);
      y += 7;
    }
  }

  addPdfFooter(pdf, true);
  return pdf;
};

/**
 * Generate PDF for Governance report
 */
export const generateGovernancePdf = async (dashboard, alerts, inconsistencies) => {
  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const totalPages = 2;
  let currentPage = 1;

  addPdfHeader(pdf, currentPage, totalPages);
  let y = 35;

  pdf.setFontSize(22);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(...COLORS.dark);
  pdf.text('Governance & Compliance Report', 15, y);
  y += 10;
  pdf.setFontSize(11);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(...COLORS.gray);
  pdf.text(`Generated: ${new Date().toLocaleDateString()}`, 15, y);
  y += 15;

  // Dashboard metrics
  if (dashboard) {
    y = addSectionTitle(pdf, 'Overview', y);
    y = addKeyValue(pdf, 'Total Classifications', String(dashboard.total_classifications || 0), y);
    y = addKeyValue(pdf, 'Consistency Score', `${dashboard.consistency_score || 0}%`, y);
    y = addKeyValue(pdf, 'Active Alerts', String(dashboard.active_alerts || 0), y);
    y = addKeyValue(pdf, 'Pending Reviews', String(dashboard.pending_reviews || 0), y);
    y += 8;
  }

  // Alerts
  if (alerts && alerts.length > 0) {
    y = addSectionTitle(pdf, `Alerts (${alerts.length})`, y);
    for (let i = 0; i < Math.min(alerts.length, 15); i++) {
      const alert = alerts[i];
      if (y > pdf.internal.pageSize.getHeight() - 40) {
        addPdfFooter(pdf, false); pdf.addPage(); currentPage++;
        addPdfHeader(pdf, currentPage, totalPages); y = 30;
      }
      pdf.setFontSize(8);
      const severity = alert.severity || 'info';
      const sColor = severity === 'high' ? [239, 68, 68] : severity === 'medium' ? [245, 158, 11] : COLORS.gray;
      pdf.setFillColor(...sColor);
      pdf.circle(19, y + 3, 1.5, 'F');
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(...COLORS.dark);
      pdf.text((alert.title || alert.message || '').substring(0, 50), 24, y + 5);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(...COLORS.gray);
      pdf.text(new Date(alert.created_at || Date.now()).toLocaleDateString(), 160, y + 5);
      y += 8;
    }
    y += 5;
  }

  // Inconsistencies
  if (inconsistencies && (inconsistencies.items || []).length > 0) {
    if (y > pdf.internal.pageSize.getHeight() - 60) {
      addPdfFooter(pdf, false); pdf.addPage(); currentPage++;
      addPdfHeader(pdf, currentPage, totalPages); y = 30;
    }
    y = addSectionTitle(pdf, 'Classification Inconsistencies', y);
    for (const item of (inconsistencies.items || []).slice(0, 10)) {
      if (y > pdf.internal.pageSize.getHeight() - 40) { break; }
      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(...COLORS.dark);
      pdf.text((item.sku_id || item.product || '').substring(0, 25), 17, y + 5);
      pdf.setFont('helvetica', 'normal');
      pdf.text(`${item.declared_hs || '-'} vs ${item.suggested_hs || '-'}`, 55, y + 5);
      pdf.setTextColor(...COLORS.gray);
      pdf.text((item.reason || '').substring(0, 40), 110, y + 5);
      y += 8;
    }
  }

  addPdfFooter(pdf, true);
  return pdf;
};

/**
 * Generate IEEPA Recovery PDF report
 */
export const generateIEEPAPdf = (scan) => {
  const pdf = new jsPDF('landscape', 'mm', 'a4');
  const pageWidth = pdf.internal.pageSize.getWidth();
  const margin = 15;
  const contentWidth = pageWidth - 2 * margin;

  // Page 1 - Executive Summary
  addPdfHeader(pdf, 1, 2);
  let y = 30;

  // Title
  pdf.setFontSize(20);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(...COLORS.primary);
  pdf.text('IEEPA Recovery Report', margin, y);
  y += 8;

  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(...COLORS.gray);
  pdf.text(`Scan: ${scan.scan_name || 'Untitled'} | Generated: ${new Date().toLocaleDateString()}`, margin, y);
  y += 12;

  // KPIs
  const kpiWidth = contentWidth / 4 - 3;
  const kpis = [
    { label: 'Total Entries', value: String(scan.total_entries || 0) },
    { label: 'Eligible Entries', value: String(scan.total_eligible_entries || 0) },
    { label: 'Potential Refund', value: `$${(scan.total_potential_refund || 0).toLocaleString()}` },
    { label: 'Total Recovery (w/ Interest)', value: `$${(scan.total_recovery || 0).toLocaleString()}` },
  ];
  kpis.forEach((kpi, i) => {
    const x = margin + (kpiWidth + 4) * i;
    pdf.setFillColor(248, 250, 252);
    pdf.roundedRect(x, y, kpiWidth, 22, 3, 3, 'F');
    pdf.setFontSize(8);
    pdf.setTextColor(...COLORS.gray);
    pdf.text(kpi.label, x + 5, y + 7);
    pdf.setFontSize(16);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(...COLORS.dark);
    pdf.text(kpi.value, x + 5, y + 18);
    pdf.setFont('helvetica', 'normal');
  });
  y += 30;

  // IEEPA Period Info
  pdf.setFillColor(255, 251, 235);
  pdf.roundedRect(margin, y, contentWidth, 18, 3, 3, 'F');
  pdf.setFontSize(9);
  pdf.setTextColor(120, 53, 15);
  pdf.setFont('helvetica', 'bold');
  pdf.text('IEEPA Period:', margin + 5, y + 7);
  pdf.setFont('helvetica', 'normal');
  pdf.text('April 2, 2025 – February 20, 2026 | Interest Rate: 5% annual | Ruling: Supreme Court 7-2 (IEEPA tariffs unconstitutional)', margin + 40, y + 7);
  pdf.setFontSize(8);
  pdf.text('Disclaimer: This report is for informational purposes only. Actual refund amounts depend on CBP/CAPE processing. Consult a licensed customs broker.', margin + 5, y + 14);
  y += 24;

  // Country Breakdown
  if (scan.results?.length > 0) {
    const countryMap = {};
    scan.results.filter(r => r.eligible).forEach(r => {
      const c = r.country_name || r.country || 'Unknown';
      if (!countryMap[c]) countryMap[c] = { count: 0, refund: 0, interest: 0, recovery: 0 };
      countryMap[c].count++;
      countryMap[c].refund += r.duty_paid || 0;
      countryMap[c].interest += r.interest_accrued || 0;
      countryMap[c].recovery += r.total_recovery || 0;
    });

    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(...COLORS.primary);
    pdf.text('Recovery by Country', margin, y);
    y += 8;

    // Table headers
    const cols = [
      { label: 'Country', x: margin, w: 60 },
      { label: 'Entries', x: margin + 65, w: 30 },
      { label: 'Duty Refund', x: margin + 100, w: 45 },
      { label: 'Interest', x: margin + 150, w: 45 },
      { label: 'Total Recovery', x: margin + 200, w: 55 },
    ];
    pdf.setFillColor(...COLORS.primary);
    pdf.roundedRect(margin, y, contentWidth, 8, 2, 2, 'F');
    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(...COLORS.white);
    cols.forEach(col => pdf.text(col.label, col.x + 3, y + 5.5));
    y += 10;

    pdf.setTextColor(...COLORS.dark);
    pdf.setFont('helvetica', 'normal');
    Object.entries(countryMap).sort((a, b) => b[1].recovery - a[1].recovery).forEach(([country, data]) => {
      if (y > 180) return;
      pdf.setFillColor(y % 2 === 0 ? 248 : 255, y % 2 === 0 ? 250 : 255, y % 2 === 0 ? 252 : 255);
      pdf.rect(margin, y - 2, contentWidth, 7, 'F');
      pdf.setFontSize(8);
      pdf.text(country.substring(0, 30), cols[0].x + 3, y + 3);
      pdf.text(String(data.count), cols[1].x + 3, y + 3);
      pdf.text(`$${data.refund.toLocaleString()}`, cols[2].x + 3, y + 3);
      pdf.text(`$${data.interest.toLocaleString()}`, cols[3].x + 3, y + 3);
      pdf.setFont('helvetica', 'bold');
      pdf.text(`$${data.recovery.toLocaleString()}`, cols[4].x + 3, y + 3);
      pdf.setFont('helvetica', 'normal');
      y += 8;
    });
  }

  addPdfFooter(pdf);

  // Page 2 - Detailed Results Table
  pdf.addPage('landscape');
  addPdfHeader(pdf, 2, 2);
  y = 28;

  pdf.setFontSize(12);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(...COLORS.primary);
  pdf.text('Detailed Entry Results', margin, y);
  y += 8;

  // Table headers
  const detailCols = [
    { label: 'Entry #', x: margin, w: 30 },
    { label: 'Date', x: margin + 32, w: 22 },
    { label: 'Country', x: margin + 56, w: 30 },
    { label: 'HS Code', x: margin + 88, w: 20 },
    { label: 'Value', x: margin + 110, w: 28 },
    { label: 'IEEPA Rate', x: margin + 140, w: 22 },
    { label: 'Duty', x: margin + 164, w: 28 },
    { label: 'Interest', x: margin + 194, w: 25 },
    { label: 'Recovery', x: margin + 221, w: 28 },
    { label: 'Status', x: margin + 251, w: 20 },
  ];
  pdf.setFillColor(...COLORS.primary);
  pdf.roundedRect(margin, y, contentWidth, 8, 2, 2, 'F');
  pdf.setFontSize(7);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(...COLORS.white);
  detailCols.forEach(col => pdf.text(col.label, col.x + 2, y + 5.5));
  y += 10;

  pdf.setTextColor(...COLORS.dark);
  pdf.setFont('helvetica', 'normal');
  (scan.results || []).slice(0, 35).forEach((r, i) => {
    if (y > 185) return;
    pdf.setFillColor(i % 2 === 0 ? 248 : 255, i % 2 === 0 ? 250 : 255, i % 2 === 0 ? 252 : 255);
    pdf.rect(margin, y - 2, contentWidth, 7, 'F');
    pdf.setFontSize(7);
    pdf.text((r.entry_number || '-').substring(0, 14), detailCols[0].x + 2, y + 3);
    pdf.text(r.entry_date || '-', detailCols[1].x + 2, y + 3);
    pdf.text((r.country_name || r.country || '-').substring(0, 14), detailCols[2].x + 2, y + 3);
    pdf.text(r.hs_code || '-', detailCols[3].x + 2, y + 3);
    pdf.text(`$${(r.customs_value || 0).toLocaleString()}`, detailCols[4].x + 2, y + 3);
    pdf.text(r.ieepa_rate || '-', detailCols[5].x + 2, y + 3);
    pdf.text(`$${(r.duty_paid || 0).toLocaleString()}`, detailCols[6].x + 2, y + 3);
    pdf.text(`$${(r.interest_accrued || 0).toLocaleString()}`, detailCols[7].x + 2, y + 3);
    pdf.setFont('helvetica', 'bold');
    pdf.text(`$${(r.total_recovery || 0).toLocaleString()}`, detailCols[8].x + 2, y + 3);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(r.eligible ? 5 : 220, r.eligible ? 150 : 38, r.eligible ? 105 : 38);
    pdf.text(r.eligible ? 'Eligible' : 'N/A', detailCols[9].x + 2, y + 3);
    pdf.setTextColor(...COLORS.dark);
    y += 7;
  });

  addPdfFooter(pdf, true);
  return pdf;
};

export default {
  generateAnalysisPdf,
  generateFinancialPdf,
  generateRefundRadarPdf,
  generateDutyRiskPdf,
  generateGovernancePdf,
  generateIEEPAPdf,
  addPdfHeader,
  addPdfFooter,
  DISCLAIMER_TEXT
};
